import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
const DOT_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6"];

// Attention dots + colour scale, drawn on top of the image.
// Shared by uploaded images (HeatmapViewer) and generated images (ImageSection).
export default function HeatmapOverlay({
  hotzones = [],
  enabled = false,
  imageSize = { width: 1, height: 1 },
}) {
  const { t } = useTranslation();
  const [visibleZones, setVisibleZones] = useState(new Set());
  const [hoveredZone, setHoveredZone] = useState(null);

  // Dots appear one by one: 1s base delay + 1s between each dot
  useEffect(() => {
    if (!enabled || hotzones.length === 0) {
      setVisibleZones(new Set());
      return;
    }
    setVisibleZones(new Set());
    const sorted = [...hotzones].sort((a, b) => a.point_order - b.point_order);
    const timeouts = sorted.map((zone, i) =>
      setTimeout(() => {
        setVisibleZones((prev) => new Set([...prev, zone.point_order]));
      }, 1000 + i * 1000)
    );
    return () => timeouts.forEach(clearTimeout);
  }, [enabled, hotzones]);

  // The scale reads low → high, so it's the dot colours reversed
  // (dot 1 is red = most attention).
  const scaleGradient = `linear-gradient(to right, ${[...DOT_COLORS].reverse().join(", ")})`;

  return (
    <>
      {/* Attention dots */}
      {enabled && hotzones.map((zone) => {
        const isVisible = visibleZones.has(zone.point_order);
        const onLeftHalf = zone.x / imageSize.width < 0.5;
        return (
          <div
            key={zone.point_order}
            className="absolute"
            style={{
              left: `${(zone.x / imageSize.width) * 100}%`,
              top: `${(zone.y / imageSize.height) * 100}%`,
              transform: "translateX(-50%) translateY(-50%)",
              zIndex: hoveredZone === zone.point_order ? 200 : 30,
            }}
            onMouseEnter={() => setHoveredZone(zone.point_order)}
            onMouseLeave={() => setHoveredZone(null)}
            onClick={() =>
              setHoveredZone((prev) => (prev === zone.point_order ? null : zone.point_order))
            }
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-black border-[3px] border-white shadow-lg ring-2 ring-black-900/40 cursor-pointer ${isVisible ?
"hotz-enter" : "hotz-hidden"}`}
              style={{ backgroundColor: DOT_COLORS[zone.point_order - 1] || "#6b7280" }}
            >
              {zone.point_order}
            </div>

            {hoveredZone === zone.point_order && (
              <div
                role="tooltip"
                className={`absolute top-1/2 -translate-y-1/2 w-48 sm:w-56 px-4 py-3 bg-black-800/90 backdrop-blur-sm text-white border-2 border-white rounded-lg shadow-xl ${
                  onLeftHalf ? "left-full ml-3" : "right-full mr-3"
                }`}
              >
                <p className="font-bold text-base mb-1">
                  {t("insights.zoneTitle", { n: zone.point_order })}
                </p>
                <p className="text-sm text-black-30">
                  {t(`insights.zoneTooltip.${zone.point_order}`)}
                </p>
              </div>
            )}
          </div>
        );
      })}

      {/* Colour scale — bottom-left pill */}
      <div
        className={`absolute left-3 bottom-3 z-10 flex items-center gap-3 px-4 py-2 rounded-lg bg-black-900/70 backdrop-blur-sm text-white text-sm font-bold pointer-events-none transition-opacity duration-500 ${
          enabled ? "opacity-100" : "opacity-0"
        }`}
      >
        <span>{t("insights.scaleLow")}</span>
        <span className="w-20 sm:w-28 h-2.5 rounded-full" style={{ background: scaleGradient }} />
        <span>{t("insights.scaleHigh")}</span>
      </div>
    </>
  );
}